import type { SlotTemplateRequestDto } from "@/modules/boxSlotTemplate/gateway/boxSlotTemplateDto"
import type { UserEntity } from "@/modules/profile/userProfile/userEntity"
import type { Weekdays } from "@/modules/boxSlotTemplate/boxSlotTemplateTypes"
import type { BoxSlotTemplateEntity } from "@/modules/boxSlotTemplate/entities/boxSlotTemplateEntity"
import dayjs from "dayjs"
import { defaultDateFormat } from "@/modules/utils/dateUtils"
import { BOX_SLOT_PARTICIPANTS_LIMIT_MIN } from "@/modules/boxSlot/boxSlotTypes"

export type BoxSlotTemplateFormikState = Omit<BoxSlotTemplateFormikEntity, "toInputDto" | "toEntity">

export class BoxSlotTemplateFormikEntity {
  id: string
  weekDay: Weekdays
  startDateTime: Date
  endDateTime: Date
  boxProgramId: string
  sessionId?: string
  participantLimit: number
  limitStart?: number
  limitEnd?: number
  limitCancel?: number
  location?: string
  allowMultiBooking: boolean
  impactAlreadyCreatedSlot?: boolean
  assignedStaffMembers?: UserEntity[]
  dropinsAccepted: boolean
  trialsAccepted: boolean

  private constructor(state: BoxSlotTemplateFormikState) {
    this.id = state.id
    this.weekDay = state.weekDay
    this.startDateTime = state.startDateTime
    this.endDateTime = state.endDateTime
    this.boxProgramId = state.boxProgramId
    this.sessionId = state.sessionId
    this.participantLimit = state.participantLimit
    this.limitStart = state.limitStart
    this.limitEnd = state.limitEnd
    this.limitCancel = state.limitCancel
    this.location = state.location
    this.allowMultiBooking = state.allowMultiBooking
    this.impactAlreadyCreatedSlot = state.impactAlreadyCreatedSlot
    this.assignedStaffMembers = state.assignedStaffMembers
    this.dropinsAccepted = state.dropinsAccepted
    this.trialsAccepted = state.trialsAccepted
  }

  static fromEntity(boxSlotTemplate: BoxSlotTemplateEntity, boxProgramId: string): BoxSlotTemplateFormikEntity {
    const formattedDay = dayjs().startOf("day").format(defaultDateFormat)

    const startDateTime = dayjs(`${formattedDay}T${boxSlotTemplate.startTime}`)
    const endDateTime = dayjs(`${formattedDay}T${boxSlotTemplate.endTime}`)

    return new BoxSlotTemplateFormikEntity({
      id: boxSlotTemplate.id,
      weekDay: boxSlotTemplate.weekDay,
      startDateTime: startDateTime.toDate(),
      endDateTime: endDateTime.toDate(),
      boxProgramId: boxProgramId,
      participantLimit: boxSlotTemplate.participantLimit ?? BOX_SLOT_PARTICIPANTS_LIMIT_MIN,
      location: boxSlotTemplate.location ?? "",
      limitStart: boxSlotTemplate.participation?.limitStart ?? undefined,
      limitEnd: boxSlotTemplate.participation?.limitEnd ?? undefined,
      limitCancel: boxSlotTemplate.participation?.limitCancel ?? undefined,
      allowMultiBooking: boxSlotTemplate.allowMultiBooking,
      impactAlreadyCreatedSlot: false,
      assignedStaffMembers: boxSlotTemplate.assignedStaffMembers,
      dropinsAccepted: boxSlotTemplate.dropinsAccepted,
      trialsAccepted: boxSlotTemplate.trialsAccepted,
    })
  }

  toInputDto(): SlotTemplateRequestDto {
    return {
      weekDay: this.weekDay,
      startTime: dayjs(this.startDateTime).format("HH:mm:ss"),
      endTime: dayjs(this.endDateTime).format("HH:mm:ss"),
      participantLimit: this.participantLimit,
      participation:
        this.limitStart == null && this.limitEnd == null && this.limitCancel == null
          ? null
          : {
              limitStart: this.limitStart ?? null,
              limitEnd: this.limitEnd ?? null,
              limitCancel: this.limitCancel ?? null,
            },
      location: this.location ?? null,
      allowMultiBooking: this.allowMultiBooking,
      impactAlreadyCreatedSlot: this.impactAlreadyCreatedSlot ?? false,
      assignedStaffMembers: this.assignedStaffMembers?.map((member) => member.id) ?? [],
      dropinsAccepted: this.dropinsAccepted,
      trialsAccepted: this.trialsAccepted,
    }
  }
}
